import type { CreateInstrumentItem, InstrumentProduct, Platform, RateType } from '../../../services/fundingApi';
import { formatDateFromBRToISO } from '../../../utils/DateUtils';
import { parseAmbiguousDecimal, parseIntegerAmount } from '../../../utils/currencyUtils';

// Módulo puro (sem React/Amplify) com a lógica de parsing de "Lançar
// Emissões" (colar planilha) - extraído de IssuanceFormPage.tsx pra poder ser
// testado com Jest sem carregar a cadeia de imports pesada do componente
// (fundingApi.ts -> aws-amplify, que quebra o parser do Jest fora de um
// ambiente de teste de componente). Ver IssuanceFormPage.test.ts.

// Mapeamento de colunas por corretora ao colar planilhas. Cada corretora
// exporta a emissão com nomes de coluna diferentes; este mapa traduz "NOME DA
// COLUNA NA PLANILHA DA CORRETORA" (em caixa alta) -> nome do campo do
// payload de POST /instruments (docs/api-contract.md). Conhecimento de
// negócio validado com a Tesouraria - não inventado.
//
// Indexado pela CONTA CETIP da plataforma (ver platformCetipKey abaixo), não
// pelo nome nem pelo id - é só uma ajuda de parsing local, o backend não sabe
// desse mapeamento. Até 2026-09-24 a chave era o NOME (normalizePlatformName)
// - trocado depois de um bug real de produção: a corretora "Nikos" foi
// renomeada para "MercadoPago" no cadastro e o fluxo especial dela (colagem
// em bloco, removida em 2026-10-09 com o template novo) sumiu
// silenciosamente, porque a chave dependia de um campo editável. CETIP é o
// identificador de negócio estável (não muda com rename, confirmado idêntico
// entre DEV e produção em 2026-09-24) e desde então o campo fica bloqueado
// para edição no modal de plataforma (ver PlatformModalForm.tsx) - a troca de
// chave só é segura por causa desse bloqueio. "GENIAL" e "GENIAL INST" são
// corretoras DISTINTAS (contas CETIP e CNPJs diferentes, confirmado com o
// usuário em 2026-09-01) - não confundir as amostras uma com a outra.
//
// Campo "indexador_raw" recebe o texto crú da coluna de índice da corretora
// (ex: "PRE", "DI", "CDI", "PRÉ-FIXADO") - normalizeRateAndIndex() abaixo
// deriva rate_type (PRE/POS) e remuneration_index a partir dele no submit.
//
// Amostras reais recebidas em 2026-08-19 (Toro, Daycoval, Genial, BTG, PicPay)
// confirmaram os campos novos (issue_date, pu, financial_value, di_futuro) para
// essas 5 corretoras - ver docs/context/business-context.md secao 8.5. Para as
// corretoras sem amostra nova (XP, AGORA, ITAU, MIRAE) só o indexador
// foi renomeado para indexador_raw; pu/financial_value/issue_date continuam
// sem mapeamento conhecido para elas (ficam para arrastar/preencher
// manualmente até termos uma amostra real de cada uma).
//
// Chaves = conta CETIP (ver seed real de GET /platforms, idêntico em
// DEV/PRD): AGORA 13492007, BTG 72080694, DAYCOVAL (LCA) 79960009, GENIAL
// 17372006, GENIAL INST 00453004, INTER 18604694, ITAU 03412004, MIRAE
// 11251006, MERCADOPAGO (ex-Nikos) 16273003, NUBANK 13042000, PICPAY 02340005,
// SANTCOR/TORO (mesma conta,
// "Toro" é o nome antigo - ver comentário mais abaixo) 26886002, XP 19352000.
export const BROKER_FIELD_MAPPING: Record<string, Record<string, string>> = {
    '79960009': {
        'TIPO IF': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA': 'tax',
        // Taxa cheia/distribuidor - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA DISTRIBUIDOR': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu',
        'PU DESAGIADO': 'pu_degassed',
        'VALOR': 'financial_value',
        'PREXDI': 'di_futuro',
    },
    // Amostra real 2026-08-31 (todas as linhas PRE): Emissor, Ativo, Indexador,
    // Emissão, Prazo Carência, Carência, Prazo (DC), Dt. Vencimento, Taxa XP,
    // Taxa Cliente, Taxa Pré (a.a.), Quantidade, PU, Financeiro, Taxa
    // Pré-Fixada XP, Taxa Pré-Fixada Cliente, Periodicidade de Juros. Pra papel
    // PRE, "Taxa XP"/"Taxa Cliente" vêm zeradas e "Taxa Pré-Fixada Cliente" é a
    // taxa cliente de fato.
    // A XP manda só UMA coluna de PU (bate exatamente com Financeiro = Quantidade
    // × PU) - sem PU de emissão (cheio) separado, diferente de Toro/Genial/
    // Daycoval/BTG. Mapeada pra pu_degassed; pu (nominal) fica sem coluna e cai
    // no default de 1000 em toCreateItem (ver DEFAULT_PU_NOMINAL) - decisão
    // tomada em 2026-08-31 sabendo que isso pode gerar alerta de divergência no
    // check de PU Desagiado pra papéis PRE de prazo longo (é só aviso, não
    // bloqueia o cadastro - ver docs/context/business-context.md §8.2).
    '19352000': {
        'ATIVO': 'product',
        'EMISSÃO': 'issue_date',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        // A XP manda taxa/taxa cheia em colunas DIFERENTES conforme o papel:
        // "TAXA PRÉ-FIXADA CLIENTE/XP" pra PRE, "TAXA CLIENTE"/"TAXA XP" pra
        // CDI/POS - dentro da MESMA planilha, sempre um dos dois pares vem
        // zerado (bug real de produção, 2026-09-15: cadastro de LCA CDI
        // rejeitado com "taxa inválida" porque só a coluna PRE estava
        // mapeada). mapPastedRows (CopyPasteTable) já escolhe, por LINHA, o
        // primeiro valor não-vazio/não-zero entre colunas que mapeiam pro
        // mesmo campo - mapear as duas resolve os dois papéis sem precisar
        // de coluna extra na tela.
        'TAXA PRÉ-FIXADA CLIENTE': 'tax',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/da própria XP - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA PRÉ-FIXADA XP': 'tax_gross',
        'TAXA XP': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
        // "FINANCEIRO" NÃO é mapeada: na XP ela é Quantidade × PU desagiado
        // (valor recebido), não o Valor Total (Quantidade × PU nominal). Ficou
        // mapeada até 2026-10-06 - bug real de produção: Despesa Plataforma
        // (Valor Total - Valor Recebido) saía R$ 0,00 em toda emissão da XP.
        // Sem a coluna, toCreateItem calcula Quantidade × PU nominal.
        // DI Futuro/curva CDI de referência pro prazo - só vem preenchida em
        // papel CDI (vazia em PRE). Achado real de produção 2026-09-16: sem
        // esse mapeamento, di_futuro ficava null pra toda linha CDI da XP -
        // o backend (check_pu_desagiado.py) trata di_futuro=None pra papel
        // POS retornando applicable=False (não roda a fórmula, não gera
        // divergência), então o efeito real era a conferência de PU
        // Desagiado ficar SILENCIOSAMENTE indisponível pra toda linha CDI
        // da XP - sem validar nada e sem avisar ninguém. Confirmado batendo
        // o mesmo valor por FAIXA DE PRAZO (mesmo vencimento -> mesma "Taxa
        // Pré (a.a.)" em linhas CDI diferentes), como esperado de uma curva
        // de referência, não de um dado por linha.
        'TAXA PRÉ (A.A.)': 'di_futuro',
    },
    // Amostra real 2026-08-19: Emissor, Ativo, Indexador, Prazo, Data Vencimento,
    // Taxa Emissor, Taxa Cliente, PU Emissão, Quantidade, Valor Aplicação,
    // Liquidez, PU Desagiado, CDI. Sem coluna de data de emissão - issue_date
    // cai no default (hoje), editável depois (ver toCreateItem).
    //
    // IMPORTANTE (confirmado com o usuário em 2026-09-01): "Toro" é o nome
    // ANTIGO desta mesma corretora - mesma conta CETIP (26886002), mesmo
    // formato de planilha/e-mail. Não existe (e nunca existiu) uma Platform
    // "TORO" cadastrada no banco - amostras rotuladas como "Toro" nesta ou
    // em sessões futuras são a SANTCOR, não uma corretora nova. Como a chave
    // aqui é a conta CETIP (não o nome), as duas já compartilham a mesma
    // entrada por definição - não precisa de uma segunda chave "TORO".
    //
    // Amostra real 2026-09-01 (10 linhas, papéis PRE e DI/POS), colada no
    // corpo de um e-mail (não planilha) - header IDÊNTICO ao de 2026-08-19
    // acima (Emissor, Ativo, Indexador, Prazo, Data Vencimento, Taxa Emissor,
    // Taxa Cliente, PU Emissão, Quantidade, Valor Aplicação, Liquidez, PU
    // Desagiado, CDI), incluindo a coluna extra de numeração de linha (1, 2,
    // 3...) que o CopyPasteTable já ignora por não bater com nenhum header
    // conhecido. Reaproveitado o mesmo mapeamento - confirmado
    // matematicamente que Valor Aplicação = Quantidade × PU Emissão (nominal
    // 1000), não PU Desagiado (ex: linha 1: 4 × 1000,00 = R$ 4.000,00).
    // Números em formato BR normal (vírgula decimal, ex: "12,50%",
    // "997,33214639", "R$ 4.000,00") - currencyToNumber padrão já dá conta
    // (sem o bug de formato americano da XP/PicPay). Datas em DD/MM/AAAA
    // normal. Sem coluna de data de emissão - cai no default de hoje, mesmo
    // padrão de 2026-08-19. Amostra extra de 3 linhas (2026-09-01, rotulada
    // "Toro" pelo usuário) confirmou também o produto CDB (além de LCA),
    // mesma conta CETIP, sem mudança de mapeamento necessária.
    '26886002': {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/emissor - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA EMISSOR': 'tax_gross',
        'PU EMISSÃO': 'pu',
        'QUANTIDADE': 'quantity',
        'VALOR APLICAÇÃO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CDI': 'di_futuro',
    },
    // Amostra real 2026-08-19 tinha uma coluna "FINANCEIRO" duplicada (bruta e
    // desagiada, mesmo nome) - resolvido em 2026-08-19 sem precisar confirmar
    // com a Genial: dá pra deduzir matematicamente qual é qual (verificado nas
    // 3 linhas da amostra) - QTDE × PU DE EMISSÃO bate exatamente com a 1ª
    // ocorrência de "FINANCEIRO" (ex: 73 × 1000 = 73.000,00) e QTDE × PU
    // DESAGIADO bate com a 2ª (ex: 73 × 999,06372357 = 72.931,65 - valor
    // desagiado, que nem pra Daycoval mapeamos). CopyPasteTable agora usa a
    // 1ª ocorrência de cada nome de coluna (fix nesta mesma revisão), então
    // mapear "FINANCEIRO" -> financial_value pega a bruta, correta.
    // "COMANDO" (número de controle da própria Genial) continua descartado -
    // decisão de negócio de 2026-08-19, ver business-context.md §8.5.
    // issue_date ainda não tem coluna confirmada (o cabeçalho colado tinha uma
    // coluna sem nome reconhecível ali) - mas como todas as linhas da amostra
    // tinham a mesma data (a do lote/lançamento), o default de "hoje" já usado
    // por Toro/Daycoval/INTER produz o mesmo resultado prático na maioria dos
    // casos - não é mais tratado como bloqueio, só uma lacuna de precisão.
    //
    // ATENÇÃO: "GENIAL" (esta chave) é uma conta CETIP diferente de "GENIAL
    // INST" (ver abaixo) - CNPJ/corretora distintos (17372006 vs 00453004 no
    // seed de plataformas), confirmado com o usuário em 2026-09-01. Não
    // confundir as amostras das duas - o mapeamento abaixo é da Genial
    // "normal" (2026-08-19), não da Institucional.
    '17372006': {
        'EMISSÃO': 'product',
        'VENCTO': 'due_date',
        'ÍNDICE': 'indexador_raw',
        'TAXA DE EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DE EMISSÃO': 'pu',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CURVA': 'di_futuro',
    },
    // Amostra real 2026-09-01 (6 linhas, todas PRE), colada no corpo de um
    // e-mail - corretora DISTINTA da "GENIAL" acima (conta CETIP 00453004,
    // "GENIAL INSTITUCIONAL CCTVM S.A.", confirmado pelo usuário; a "GENIAL"
    // simples é 17372006, entidade diferente). Bug real reportado pelo
    // negócio como "Genial inst – ordem do cabeçalho errada" -
    // **inicialmente corrigido por engano na chave GENIAL (revertido) antes
    // de perceber que era essa corretora aqui**.
    //
    // Header tem 14 colunas nomeadas + 1 coluna de Emissor SEM nome no
    // cabeçalho na frente (mesmo padrão do SANTCOR - "Agrolend", constante
    // em toda linha) - 15 valores por linha de dado. Essa é a mesma coluna
    // sem nome que a sessão de 2026-08-19 já tinha detectado na Genial
    // "normal" sem saber o que era - agora confirmado (nesta corretora, ao
    // menos) que é o Emissor, não issue_date.
    // - "TÍTULO" é o PRODUTO ("LCA").
    // - "EMISSÃO" é a DATA DE EMISSÃO ("31/08/2026").
    // - "TAXA CHEIA" (maior, ex: 13,35) é a taxa cheia/emissor - não
    //   mapeada; "TAXA DE EMISSÃO" (menor, ex: 12,95) é a taxa de CLIENTE,
    //   apesar do nome sugerir o contrário - confirmado por ser sempre a
    //   menor das duas (mesmo padrão cliente <= emissor de outras
    //   corretoras).
    // Números em formato BR normal (vírgula decimal, sem "%" - ex: "13,35",
    // não "13,35%"), currencyToNumber padrão já dá conta. Datas em
    // DD/MM/AAAA normal. "CURVA" manda "0" literal em papel PRE (mesmo
    // mecanismo de normalização usado pra "GENIAL" e Daycoval/"NaN").
    '00453004': {
        'TÍTULO': 'product',
        'EMISSÃO': 'issue_date',
        'VENCTO': 'due_date',
        'ÍNDICE': 'indexador_raw',
        'TAXA DE EMISSÃO': 'tax',
        // Mapeada em 2026-09-02 pra alimentar a conferência de PU Desagiado
        // (duas taxas) - ver docs/context/business-context.md §8.9.
        'TAXA CHEIA': 'tax_gross',
        'QTDE': 'quantity',
        'PU DE EMISSÃO': 'pu',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CURVA': 'di_futuro',
    },
    // MERCADOPAGO (ex-Nikos). Até 2026-10-09 mandava uma "memória de cálculo"
    // por operação, em células fixas, lida por colagem em bloco. Template novo
    // (arquivo real de 2026-10-09) é uma tabela: Emissor, Volume, Quantidade,
    // Produto, Indexador, Taxa cliente, Taxa DTVM, Data de vencimento, Dias
    // corridos, Dias Úteis, DI, PU Desagiado, Data da operação, Valor
    // financeiro, PU Emissão. "Volume" é Quantidade × PU Emissão (Valor
    // Total); "Valor financeiro" é Quantidade × PU Desagiado (valor recebido)
    // e NÃO é mapeado - mesmo erro corrigido na XP/PicPay em 2026-10-06.
    '16273003': {
        'PRODUTO': 'product',
        'DATA DA OPERAÇÃO': 'issue_date',
        'DATA DE VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'TAXA DTVM': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU EMISSÃO': 'pu',
        'PU DESAGIADO': 'pu_degassed',
        'VOLUME': 'financial_value',
        'DI': 'di_futuro',
    },
    '13492007': {
        'ATIVO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TX EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESÁGIO': 'pu_degassed',
    },
    // Colunas reais confirmadas no bug do produto "CDB PRE" (2026-08-19): TIPO DE
    // CONTA, CONTA, ATIVO, EMISSOR, PRAZO/VENCIMENTO, FINANCEIRO, PU, QUANTIDADE,
    // TAXA CLIENTE (EMISSÃO), TAXA INTER DTVM (CHEIA), PU Desagiado, INDEXADOR,
    // DIAS C, DIAS U, DI, BROKER. Sem coluna de data de emissão.
    '18604694': {
        'ATIVO': 'product',
        'PRAZO / VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE (EMISSÃO)': 'tax',
        // Taxa cheia/DTVM - mapeada em 2026-09-03 a partir de amostra real (nunca
        // tinha sido conferida antes) pra alimentar a conferência de PU Desagiado
        // (duas taxas) - ver docs/context/business-context.md §8.9.
        'TAXA INTER DTVM (CHEIA)': 'tax_gross',
        'PU': 'pu',
        'QUANTIDADE': 'quantity',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'DI': 'di_futuro',
    },
    '03412004': {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    // Amostra real 2026-08-19: Produto, Prazo (Mês), Data Emissão, Data
    // Vencimento, DC, DU, Liquidez, Data de Início da Liquidez, Indexador,
    // Curva DI, Fee (a.a.), Taxa All, Taxa Cliente, Quantidade, PU Emissão,
    // Emissão (R$), PU Desagiado. Nomes de coluna confirmados de novo com
    // amostra completa de 6 linhas em 2026-09-01 - mapeamento em si (nomes)
    // já estava certo, mas achado bug real de FORMATO DE NÚMERO por coluna
    // (hoje resolvido pelo parseAmbiguousDecimal, que acerta os dois formatos
    // sem precisar saber qual é qual): "CURVA DI" e "EMISSÃO (R$)" vinham em
    // formato americano (ex: "14.49%", "75,000.00") dentro da MESMA
    // planilha onde "TAXA CLIENTE"/"PU EMISSÃO"/"PU DESAGIADO" vêm em
    // formato BR normal (ex: "99,00%", "1.000,00", "995,98186417") -
    // confirmado matematicamente que Quantidade × PU Emissão bate com
    // Emissão (R$) só lendo essa coluna como americana.
    '72080694': {
        'PRODUTO': 'product',
        'DATA EMISSÃO': 'issue_date',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'CURVA DI': 'di_futuro',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/all-in (sempre 100,00% nas amostras vistas) - mapeada em
        // 2026-09-02 pra alimentar a conferência de PU Desagiado (duas
        // taxas) - ver docs/context/business-context.md §8.9.
        'TAXA ALL': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU EMISSÃO': 'pu',
        'EMISSÃO (R$)': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
    },
    // Duas fontes reais pra mesma corretora, cabeçalhos diferentes - confirmado
    // em 2026-09-02 com a MESMA amostra (20 linhas, PRE e POS/CDI) recebida tanto
    // colada no corpo do e-mail (ATIVO/TX EMISSOR/TX CLIENTE/QTDE) quanto no
    // Excel anexo (TITULO/TAXA EMISSOR/TAXA CLIENTE/QTDE TOTAL) - por isso as
    // duas grafias de cada campo aparecem aqui. O mapeamento antigo só cobria a
    // variante do Excel e não tinha tax_gross/di_futuro/PU EMISSAO (pu nominal)
    // - nenhuma amostra real tinha sido conferida antes.
    '13042000': {
        'ATIVO': 'product',
        'TITULO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TX CLIENTE': 'tax',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/emissor - mapeada em 2026-09-02 pra alimentar a conferência
        // de PU Desagiado (duas taxas) - ver docs/context/business-context.md §8.9.
        'TX EMISSOR': 'tax_gross',
        'TAXA EMISSOR': 'tax_gross',
        'QTDE': 'quantity',
        'QTDE TOTAL': 'quantity',
        // Taxa de referência (CDI) do prazo, presente em toda linha - inclusive
        // papel Pré (informativo; a conferência de PU Desagiado só usa di_futuro
        // pra papel POS, ignora pra PRE - ver check_pu_desagiado.py no backend).
        'DI': 'di_futuro',
        'PU': 'pu_degassed',
        'PU EMISSAO': 'pu',
    },
    '11251006': {
        'ATIVO': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    // Amostra real 2026-09-01 (arquivo completo, não só a amostra de
    // 2026-08-19): Emissor, Ativo, Indexador, Emissão, Prazo Carência,
    // Carência, Prazo (DC), Dt. Vencimento, Taxa, Taxa Cliente, Quantidade,
    // PU, Financeiro. A PicPay manda só UMA coluna de PU (bate exatamente com
    // Financeiro = Quantidade × PU, conferido linha a linha) - é o PU
    // desagiado, não o nominal, mesmo padrão da XP (ver comentário lá em
    // cima). "PU" mapeado pra pu_degassed; pu (nominal) fica sem coluna e cai
    // no default de 1000 em toCreateItem. Mapeamento anterior (2026-08-19)
    // tinha isso invertido ('PU': 'pu') com base numa amostra menor onde a
    // conta não fechava - corrigido em 2026-09-01 com o arquivo real completo.
    '02340005': {
        'ATIVO': 'product',
        'EMISSÃO': 'issue_date',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia (coluna "Taxa", distinta de "Taxa Cliente") - mapeada em
        // 2026-09-02 pra alimentar a conferência de PU Desagiado (duas
        // taxas) - ver docs/context/business-context.md §8.9.
        'TAXA': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
        // "FINANCEIRO" NÃO é mapeada, mesmo motivo da XP (ver lá em cima): é
        // Quantidade × PU desagiado, não o Valor Total (bug de 2026-10-06).
    },
};

// PicPay envia issue_date em M/D/AAAA (US) mas due_date em DD/MM/AAAA (BR) -
// formato varia por COLUNA dentro da mesma planilha, não por corretora como
// um todo (confirmado inspecionando o number_format real das células do
// Excel: "Emissão" usa "mm-dd-yy", "Dt. Vencimento" usa "dd/mm/yyyy"). Por
// isso a chave é "CONTA_CETIP:campo", não só a plataforma - ver toCreateItem.
//
// Chave é a conta CETIP (platformCetipKey), não o nome - achado real do
// Copilot no PR que trocou BROKER_FIELD_MAPPING pra CETIP (2026-09-24): essa
// configuração ficou de fora da primeira troca e continuaria quebrando com
// um rename futuro da PicPay, exatamente como aconteceu com a Nikos.
export const US_DATE_FORMAT_FIELDS = new Set(['02340005:issue_date']);

// NOTA (2026-09-09): existiam aqui US_NUMBER_FORMAT_PLATFORMS e
// US_NUMBER_FORMAT_FIELDS, que assumiam formato de número fixo por corretora
// (XP/PicPay/Nikos americano) e por campo (BTG: "CURVA DI"/"EMISSÃO (R$)").
// Removidos: a suposição quebrou em produção quando XP e PicPay mandaram
// planilha em formato brasileiro e o parser americano inflou os valores
// ("12,00" -> 1200), estourando o NUMERIC(15,6) do banco. O formato depende
// da config regional de quem exporta o arquivo, não da corretora - hoje todo
// campo numérico usa parseAmbiguousDecimal (último separador = decimal), que
// acerta os dois formatos. Datas continuam com flag por campo
// (US_DATE_FORMAT_FIELDS acima): lá a ambiguidade é real e insolúvel por
// heurística ("07/06/2027" é válido nos dois formatos, com sentidos
// diferentes), então continua precisando de conhecimento por corretora.

// Layout de destino da colagem - igual para qualquer plataforma (o que muda
// de plataforma para plataforma é o BROKER_FIELD_MAPPING acima). issue_date e
// di_futuro não são required: quando a corretora não manda a coluna (ver
// mapeamentos acima), issue_date cai no default de hoje e di_futuro fica nulo.
export const ISSUANCE_HEADERS = [
    { label: 'Produto', field: 'product', required: true, format: 'text' },
    { label: 'Data Emissão', field: 'issue_date', required: false, format: 'text' },
    { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
    { label: 'Indexador', field: 'indexador_raw', required: true, format: 'text' },
    { label: 'Taxa', field: 'tax', required: true, format: 'text' },
    // Taxa cheia/all-in/emissor/distribuidor - segunda taxa usada na conferência
    // de PU Desagiado (ver check_pu_desagiado.py no backend). Opcional: sem ela
    // a conferência fica indisponível, mas o cadastro em si não é bloqueado -
    // nem toda corretora manda essa coluna mapeada ainda.
    { label: 'Taxa Cheia', field: 'tax_gross', required: false, format: 'text' },
    { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
    // Não obrigatório na colagem: XP/AGORA/ITAU/MIRAE não mandam PU
    // de emissão (cheio) separado do desagiado - cai no default de 1000 em
    // toCreateItem (ver DEFAULT_PU_NOMINAL) quando a coluna não é mapeada.
    { label: 'PU', field: 'pu', required: false, format: 'text' },
    // Não obrigatório na coluna: produto DI não tem PU Desagiado (ver
    // requiresPuDegassed em IssuanceFormPage.tsx) e a validação por coluna do
    // CopyPasteTable roda ANTES do toCreateItem/itemProblems saber o produto da
    // linha - com required:true aqui, uma planilha só de DI era rejeitada por
    // completo antes da exceção de DI poder ser aplicada (bug real). A
    // obrigatoriedade para LCA/CDB continua sendo aplicada por itemProblems.
    { label: 'PU Desagiado', field: 'pu_degassed', required: false, format: 'text' },
    // Opcional - só DI usa (ver requiresAdditionalSpread na emissão manual).
    // Nenhuma corretora com amostra real confirmada manda essa coluna hoje;
    // fica disponível para mapeamento manual (drag-and-drop) quando alguma
    // mandar, em vez de ficar restrito só à emissão manual.
    { label: 'Spread Adicional', field: 'additional_spread', required: false, format: 'text' },
    // Não obrigatório na colagem (mesmo motivo do `pu` acima): NUBANK não manda
    // Valor/Financeiro em nenhuma das duas fontes reais (e-mail nem Excel anexo)
    // - toCreateItem calcula Quantidade × PU quando a coluna não é mapeada (ver
    // comentário lá). Bug real encontrado em 2026-09-02: com `required: true`
    // aqui, o CopyPasteTable bloqueava o Salvar ("Coluna Valor é obrigatória")
    // ANTES do cálculo rodar (esse cálculo só acontece no submit, dentro de
    // toCreateItem - a validação por coluna roda antes, em cima do dado cru).
    { label: 'Valor', field: 'financial_value', required: false, format: 'text' },
    { label: 'DI Futuro', field: 'di_futuro', required: false, format: 'text' },
];

// Chave de identidade usada em BROKER_FIELD_MAPPING e US_DATE_FORMAT_FIELDS -
// a conta CETIP (LCA,
// senão CDB, senão DI) é o identificador estável da plataforma, ao contrário
// do nome (editável no cadastro - ver PlatformModalForm.tsx, que trava esse
// campo justamente por causa disso). Prioriza LCA porque é a única plataforma
// com conta diferente por produto (Daycoval, LCA 79960009 / CDB 79960690) -
// as demais usam o mesmo valor nas duas.
export const platformCetipKey = (
    platform: Pick<Platform, 'cetipAccountLca' | 'cetipAccountCdb' | 'cetipAccountDi'>,
): string => (platform.cetipAccountLca || platform.cetipAccountCdb || platform.cetipAccountDi || '').trim();

export type PastedRow = Record<string, string>;

// Converte "DD/MM/AAAA" (planilha, padrão BR) ou "M/D/AAAA" (PicPay, formato
// americano - ver US_DATE_FORMAT_FIELDS) ou "AAAA-MM-DD" (já no formato da
// API) para o formato ISO exigido por docs/api-contract.md ("YYYY-MM-DD").
export const toIsoDate = (raw: string, useUsFormat: boolean): string => {
    const trimmed = (raw || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    if (useUsFormat) {
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usMatch) {
            const [, month, day, year] = usMatch;
            // Heurística de segurança (mesmo padrão já aplicado aos números
            // em 2026-09-09, ver toCreateItem abaixo): quando o "mês"
            // extraído pela leitura americana (MM/DD) é > 12, o valor não
            // pode estar em formato americano de verdade - cai pra BR
            // (DD/MM) em vez de montar uma data inválida. Achado real em
            // produção (2026-09-14): a PicPay nem sempre manda issue_date em
            // formato americano (ex: "14/09/2026" rejeitado pelo backend com
            // "month must be in 1..12" ao ser lido como mês=14).
            if (Number(month) <= 12) {
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
        }
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return formatDateFromBRToISO(trimmed);
    return trimmed;
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// PU de emissão (cheio) de LCA/CDB é, na prática, sempre 1000 nas amostras
// reais vistas (ver docs/context/business-context.md §8.5) - usado quando a
// corretora não manda essa coluna separada do PU desagiado (ex: XP).
export const DEFAULT_PU_NOMINAL = 1000;

// Algumas corretoras (ex: INTER) juntam produto e indexador na mesma coluna
// "ATIVO" (ex: "CDB PRE", "LCA PRE"), embora o indexador já venha separado na
// coluna INDEXADOR. Aqui extraímos só o código do produto (LCA/CDB), ignorando
// qualquer sufixo - sem isso "CDB PRE" nunca bate com o enum exato aceito pela API.
export const normalizeProduct = (raw: string): string => {
    const upper = (raw || '').trim().toUpperCase();
    const match = upper.match(/^(LCA|CDB)\b/);
    return match ? match[1] : upper;
};

// Toda corretora usa uma grafia diferente para indicar pré vs pós-fixado na
// mesma coluna (Toro/PicPay: "PRE"/"DI"; Daycoval/BTG: "PRE"/"CDI"; Genial:
// "PRÉ-FIXADO"/"CDI"; INTER: "LCA PRE"/"LCA POS", literalmente "POS" - único
// caso visto até agora, amostra real 2026-09-03). "DI" como indexador é
// convenção de mercado para papel indexado ao CDI, por isso mapeia pra
// remuneration_index="CDI" (ver docs/context/business-context.md secao 8.5).
// A INTER não manda o nome do índice separado (só "POS"/"PRE" na mesma coluna
// do rate_type) - mesma convenção CDI, confirmada pela coluna "DI" (curva CDI
// futura) só vir preenchida nas linhas POS da amostra real.
export const normalizeRateAndIndex = (raw: string): { rateType: RateType; remunerationIndex: string | null } => {
    const upper = (raw || '').trim().toUpperCase();
    if (upper.startsWith('PRE') || upper.includes('PRÉ')) {
        return { rateType: 'PRE', remunerationIndex: null };
    }
    if (upper === 'DI' || upper === 'POS') {
        return { rateType: 'POS', remunerationIndex: 'CDI' };
    }
    return { rateType: 'POS', remunerationIndex: upper || null };
};

export const toCreateItem = (row: PastedRow, platformId: string, platformCetip: string): CreateInstrumentItem => {
    const useUsFormatIssueDate = US_DATE_FORMAT_FIELDS.has(`${platformCetip}:issue_date`);
    const useUsFormatDueDate = US_DATE_FORMAT_FIELDS.has(`${platformCetip}:due_date`);
    // Números usam a heurística ambígua (último separador = decimal) pra TODAS
    // as corretoras desde 2026-09-09. Antes havia uma suposição fixa de formato
    // por corretora/campo (US_NUMBER_FORMAT_PLATFORMS/FIELDS) e ela quebrou em
    // produção: XP e PicPay, que estavam marcadas como formato americano,
    // mandaram uma planilha em formato brasileiro e o parser inflou tudo
    // ("12,00" -> 1200, "984,224435920" -> 984224435920), estourando o
    // NUMERIC(15,6) do banco. O formato depende da config regional de quem
    // exporta o arquivo, não da corretora - mesma descoberta já feita na Nikos.
    const parseNumFor = (_field: string) => parseAmbiguousDecimal;
    const { rateType, remunerationIndex } = normalizeRateAndIndex(row.indexador_raw);
    // Genial manda "0" literal em vez de vazio na coluna CURVA quando o papel é
    // PRE (sem DI Futuro aplicável) - trata como ausência de valor, não como
    // taxa zero de fato.
    const parsedDiFuturo = parseNumFor('di_futuro')(row.di_futuro || '');
    // Quantidade usa o parser de inteiro (ver parseIntegerAmount): um separador
    // sozinho ali é milhar, não decimal - o oposto da regra dos campos decimais.
    const quantity = parseIntegerAmount(row.quantity || '');
    const pu = row.pu ? parseNumFor('pu')(row.pu) : DEFAULT_PU_NOMINAL;
    const product = normalizeProduct(row.product) as InstrumentProduct;

    return {
        product,
        // Toro/Daycoval/INTER não mandam data de aplicação na planilha - cai no
        // default de hoje, editável depois (ver decisão do negócio em 2026-08-19).
        issue_date: row.issue_date ? toIsoDate(row.issue_date, useUsFormatIssueDate) : todayIso(),
        due_date: toIsoDate(row.due_date, useUsFormatDueDate),
        rate_type: rateType,
        remuneration_index: remunerationIndex,
        tax: parseNumFor('tax')(row.tax || ''),
        tax_gross: row.tax_gross ? parseNumFor('tax_gross')(row.tax_gross) : null,
        quantity,
        pu,
        // DI não tem PU Desagiado (ver requiresPuDegassed) - manda null em vez
        // de "0" (que passava despercebido como valor real e/ou estourava a
        // validação do backend). Mesma regra quando a coluna está vazia para
        // os demais produtos: null deixa itemProblems apontar o erro.
        pu_degassed: (product !== 'DI' && row.pu_degassed) ? parseNumFor('pu_degassed')(row.pu_degassed) : null,
        // Sem coluna própria (ex: NUBANK, nem no e-mail nem no Excel anexo - só
        // Quantidade e PU Emissão), calcula Quantidade × PU nominal - mesma
        // convenção já confirmada matematicamente pra Toro/SANTCOR/Daycoval
        // (decisão do usuário, 2026-09-02).
        financial_value: row.financial_value ? parseNumFor('financial_value')(row.financial_value) : quantity * pu,
        di_futuro: parsedDiFuturo > 0 ? parsedDiFuturo : null,
        // Só DI usa (ver ISSUANCE_HEADERS acima) - nenhuma corretora mapeada
        // manda essa coluna hoje, só disponível via drag-and-drop manual.
        additional_spread: (product === 'DI' && row.additional_spread) ? parseNumFor('additional_spread')(row.additional_spread) : null,
        platform_id: platformId,
    };
};
